import { useState, useEffect, useMemo, useRef } from "react";

import { Modal } from "flowbite-react";

import { useGoals, useToast } from "../../hooks";
import { useFeatureFlag, FEATURE_FLAGS } from "../../contexts";
import { NOTIFICATION_TYPES, getFormattedMoney } from "../../utils";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import {
  RenewIcon,
  TrendUpIcon,
  PilotsIcon,
} from "../../pages/DashboardV2Page/icons";
import { PilotsNotMappedCard } from "../../pages/DashboardV2Page/PilotsNotMappedCard";
import { useGoalAmountFormat } from "../../pages/DashboardV2Page/useGoalAmountFormat";
import { FrozenBaseLine } from "../../pages/DashboardV2Page/FrozenBaseLine";
import {
  DASHBOARD_GOAL_TYPES,
  GOAL_DEFAULTS,
  getDashboardGoals,
  getTargetPct,
  getPilotPool,
  getCount,
  computeEditingTargets,
  getBackendQuarterLabel,
  getQuarterLabel,
  getQuarterEndDate,
  getSummaryCurrency,
} from "../../pages/DashboardV2Page/constants";
import { revenueMetricsService } from "../../services/RevenueMetricsService";
import { dashboardSummaryService } from "../../services/DashboardSummaryService";

// Three-bucket additive (PAN-520): Renewal + Expansion + Pilots. Save/Attrition retired
// — an 80% renewal goal already implies a 20% loss goal by definition, so a Save slider
// would double-count Renewal. Pilots slider auto-hides when pilots_mapped=false, matching
// the widget's auto-hide behavior.
const SLIDER_CONFIG = [
  {
    goalType: DASHBOARD_GOAL_TYPES.retention,
    label: "Retention Target",
    icon: RenewIcon,
    iconClass: "text-amber-700",
    labelClass: "text-amber-900",
    bgClass: "bg-amber-50",
    borderClass: "border-amber-200",
    valueClass: "text-amber-500",
    fillColor: "rgb(245,158,11)",
    summaryKey: "renewal",
    question: "What % of renewals should the team close?",
    summaryColor: "bg-amber-400",
    summaryLabel: "Renewals",
    baseLabel: "Renewal cohort",
    historyNote: "Historical: 91%",
    min: 70,
    max: 100,
    step: 1,
  },
  {
    goalType: DASHBOARD_GOAL_TYPES.expansion,
    label: "Expansion Target",
    icon: TrendUpIcon,
    iconClass: "text-emerald-700",
    labelClass: "text-emerald-900",
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-200",
    valueClass: "text-emerald-500",
    fillColor: "rgb(16,185,129)",
    summaryKey: "expansion",
    question: "What % of expansion pipeline should convert?",
    summaryColor: "bg-emerald-500",
    summaryLabel: "Expansion",
    baseLabel: "Expansion in play",
    historyNote: null,
    min: 0,
    max: 100,
    step: 5,
  },
  {
    goalType: DASHBOARD_GOAL_TYPES.pilot,
    label: "Pilot Conversion Target",
    icon: PilotsIcon,
    iconClass: "text-indigo-700",
    labelClass: "text-indigo-900",
    bgClass: "bg-indigo-50",
    borderClass: "border-indigo-200",
    valueClass: "text-indigo-500",
    fillColor: "rgb(99,102,241)",
    summaryKey: "pilots",
    question: "What % of pilots should convert?",
    summaryColor: "bg-indigo-500",
    summaryLabel: "Pilots",
    baseLabel: "In-play pilot MRR",
    historyNote: null,
    min: 0,
    max: 100,
    step: 5,
    requiresPilotsMapped: true,
  },
];

// Portfolio-stats row (PAN-638): each figure is the base its slider multiplies
// (computeEditingTargets): the renewal cohort this quarter, the expansion base, the
// in-play pilots ending this quarter, from the NRR goal-progress response. It used
// to read the dashboard summary's account categories, a different calculation, which
// put "Renewals due €0" next to a goal built on €108. Two known gaps with the widget
// remain: Expansion shows the LIVE base where a committed goal shows the base at
// commit time (the modal is a calculator for the next save), and a bucket with no
// goal shows 0 here while the widget falls back to summary × %. With no NRR
// response the base falls back to the summary, as in the widget. The response
// carries no account counts, so the row shows none.
// Behind ESTIMATED_GOAL_PILL; flag off keeps the pre-PAN-638 row, where
// `legacySource` says which response each figure was read from.
const PORTFOLIO_STATS = [
  { label: "Renewals due", key: "renewal", legacySource: "summary" },
  { label: "Expansion pipeline", key: "expansion", legacySource: "summary" },
  {
    label: "Pilots in play",
    key: "pilots",
    legacySource: "nrrProgress",
    requiresPilotsMapped: true,
  },
];

// PAN-533-followup: mirror of RevenueGoalCard's annualize.
// Numbers stored in Goal DB rows stay MRR; the toggle is a display transform.
const annualize = (value, revenueUnit) =>
  revenueUnit === "ARR" ? value * 12 : value;

// PAN-638: the modal's figures come from the NRR goal-progress response, so its
// currency does too; the summary's is the fallback when that response has none.
// Flag off: the summary's, as before.
const modalCurrency = (summary, nrrProgress, flagOn) =>
  flagOn
    ? (nrrProgress?.currency ?? getSummaryCurrency(summary))
    : getSummaryCurrency(summary);

// PAN-639: the backend's goal floor — below it no goal exists, so no preview.
const GOAL_FLOOR = 1;

export const TeamGoalsModal = ({ open, onClose, revenueUnit = "MRR" }) => {
  const { goals, createGoal, updateGoal } = useGoals();

  // PAN-587: the modal fetches its own TEAM-WIDE data on every open. A team goal
  // is a team-level setting, so it must be previewed against the whole team's
  // base even when the sidebar is on "My Customers" (the sidebar scope is a
  // viewing filter, not a write scope). Save stays disabled until both settle.
  const [data, setData] = useState({
    summary: null,
    nrrProgress: null,
    loaded: false,
  });
  const { summary, nrrProgress } = data;

  useEffect(() => {
    if (!open) {
      // PAN-639: forget the previous response on close, so a reopen never
      // seeds sliders from it before the new one lands.
      setData((prev) =>
        prev.summary || prev.nrrProgress || prev.loaded
          ? { summary: null, nrrProgress: null, loaded: false }
          : prev,
      );
      return undefined;
    }
    let cancelled = false;
    setData({ summary: null, nrrProgress: null, loaded: false });
    Promise.allSettled([
      dashboardSummaryService.getSummary({ teamWide: true }),
      revenueMetricsService.getNrrGoalProgress({ teamWide: true }),
    ]).then(([summaryResult, nrrResult]) => {
      if (cancelled) return;
      // Either may fail — the modal degrades to the legacy pools (both null).
      setData({
        summary:
          summaryResult.status === "fulfilled" ? summaryResult.value : null,
        nrrProgress: nrrResult.status === "fulfilled" ? nrrResult.value : null,
        loaded: true,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Memoize to avoid infinite useEffect re-triggers
  const existingGoals = useMemo(() => getDashboardGoals(goals), [goals]);

  const [values, setValues] = useState({});
  // PAN-587: per-bucket "did the user touch this slider" flag. Behind
  // ESTIMATED_GOAL_PILL, an untouched slider must NOT convert the shown live
  // estimate into a leader commit (that is the retroactive-commit harm the
  // dormant cap used to protect against; the fix belongs in the modal).
  const [didUserTouchSlider, setDidUserTouchSlider] = useState({});
  const [saving, setSaving] = useState(false);
  const addToast = useToast();
  const saveGateEnabled = useFeatureFlag(FEATURE_FLAGS.ESTIMATED_GOAL_PILL);
  // PAN-639: the renewal goal freezes; the modal shows what it was set against,
  // hides a preview below the floor, and commits Renewal on Save.
  const renewalFrozenFlag = useFeatureFlag(FEATURE_FLAGS.RENEWAL_GOAL_FROZEN);
  // The backend's rule (renewal_goal_frozen_enabled) needs estimated_goal_pill
  // too; the touched-only Save below hangs off it.
  // The backend's own verdict (renewal_goal_frozen_enabled — both flags AND a
  // fiscal year starting in January) is in the response: renewal.live_base_mrr
  // is sent only when the freeze is really on. Without the widget's data, the
  // modal behaves as before.
  const renewalFrozenEnabled =
    renewalFrozenFlag &&
    saveGateEnabled &&
    Boolean(nrrProgress?.renewal) &&
    "live_base_mrr" in nrrProgress.renewal;
  // PAN-796: the pilots goal is a conversion rate committed per quarter. The
  // backend's verdict is in the response: pilots.live_base_mrr (the quarter's
  // PoC cohort) is sent only when the backend commits pilots for this team.
  const pilotsCommitEnabled =
    saveGateEnabled &&
    Boolean(nrrProgress?.pilots) &&
    "live_base_mrr" in nrrProgress.pilots;
  const fmt = useGoalAmountFormat();

  const pilotsMapped = Boolean(nrrProgress?.pilots_mapped);
  const visibleSliders = SLIDER_CONFIG.filter(
    (cfg) => !cfg.requiresPilotsMapped || pilotsMapped,
  );
  const visiblePortfolioStats = PORTFOLIO_STATS.filter(
    (stat) => !stat.requiresPilotsMapped || pilotsMapped,
  );

  // Any slider touched → Save is meaningful; none touched with the gate on →
  // Save would only re-commit a live estimate, which we refuse (PAN-587).
  const anySliderTouched = Object.values(didUserTouchSlider).some(Boolean);

  // PAN-587: when the modal OPENS, seed every slider from the saved goals and
  // clear the touched flags. When `goals` changes while the modal is already
  // open — goals that load after opening, or Save's own legacy Goal writes —
  // re-seed only the UNTOUCHED sliders and leave touched sliders' values and
  // flags alone. Re-seeding a touched slider would discard the leader's edit
  // (and a failed Expansion commit could not be retried); leaving an untouched
  // slider on its default would let a flag-off Save overwrite the real saved
  // target with that default.
  // PAN-639: the % an untouched slider shows. With the renewal freeze on and
  // this open's data loaded, the % its goal was set (or estimated) at, so the
  // card and the summary's goal in force agree; otherwise the saved legacy %.
  // Read through a ref by both seeding effects, so neither can undo the other.
  const seedPctFor = (cfg) => {
    // PAN-796: with no pilots goal (cohort below the floor) the backend still
    // sends the quarter's pilots rate, so the slider never falls back to a
    // legacy % that Save would then commit.
    const raw =
      nrrProgress?.[cfg.summaryKey]?.goal?.pct_at_set ??
      (pilotsCommitEnabled && cfg.summaryKey === "pilots"
        ? nrrProgress?.pilots?.pct
        : null);
    const goalPct = raw == null ? NaN : Number(raw);
    // PAN-796: pilots seed from the goal's % too — the quarter's committed rate,
    // or (estimated) the last quarter's.
    const seedFromGoal =
      renewalFrozenEnabled ||
      (pilotsCommitEnabled && cfg.summaryKey === "pilots");
    return seedFromGoal && data.loaded && Number.isFinite(goalPct)
      ? goalPct
      : getTargetPct(existingGoals, cfg.goalType);
  };
  const seedPctRef = useRef(seedPctFor);
  seedPctRef.current = seedPctFor;

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) return;
    const seeded = {};
    for (const cfg of SLIDER_CONFIG) {
      seeded[cfg.goalType] = seedPctRef.current(cfg);
    }
    if (justOpened) {
      setValues(seeded);
      // Keep the same (empty) object when nothing is touched, so opening does
      // not change the flags' identity and re-run this effect.
      setDidUserTouchSlider((prev) =>
        Object.values(prev).some(Boolean) ? {} : prev,
      );
      return;
    }
    // `didUserTouchSlider` is a dependency, so these are the current flags;
    // the functional update reads the current values. Returning `prev` when
    // nothing changed keeps `values` identity unchanged; it does NOT make the
    // effect free — a flags change still re-runs it, and React renders once
    // more to evaluate the updater (measured: the first touch of a slider
    // costs 2 commits, later ticks of the same slider cost 1).
    //
    // Accepted rare race: if a goals change has committed but this effect has
    // not yet run when a slider change event lands, the pending re-seed can
    // overwrite that one tick of an UNTOUCHED slider (its flag is not yet in
    // this effect's closure). The next input event restores the user's value.
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [goalType, pct] of Object.entries(seeded)) {
        if (didUserTouchSlider[goalType] || prev[goalType] === pct) continue;
        next[goalType] = pct;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [open, existingGoals, didUserTouchSlider]);

  // PAN-639: when this open's data lands, move untouched sliders to their
  // seed % (seedPctFor). The seeding effect above uses the same rule, so a
  // touch or a goals refetch cannot snap them back to the legacy %.
  const touchedRef = useRef(didUserTouchSlider);
  touchedRef.current = didUserTouchSlider;
  useEffect(() => {
    if (!open || !(renewalFrozenEnabled || pilotsCommitEnabled) || !data.loaded)
      return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const cfg of SLIDER_CONFIG) {
        if (touchedRef.current[cfg.goalType]) continue;
        const pct = seedPctRef.current(cfg);
        if (prev[cfg.goalType] !== pct) {
          next[cfg.goalType] = pct;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    open,
    nrrProgress,
    renewalFrozenEnabled,
    pilotsCommitEnabled,
    data.loaded,
  ]);

  const getValue = (goalType) =>
    values[goalType] ?? GOAL_DEFAULTS[goalType] ?? 0;

  // PAN-639: a Retention change is committed only with the freeze on, the
  // slider touched, the widget's data loaded (the server stamps its own live
  // base) and the target at or above the floor (below it there is no goal).
  const retentionPreview = () =>
    computeEditingTargets(summary, nrrProgress, {
      retention: getValue(DASHBOARD_GOAL_TYPES.retention),
      expansion: getValue(DASHBOARD_GOAL_TYPES.expansion),
      pilot: getValue(DASHBOARD_GOAL_TYPES.pilot),
    }).renewal.target;
  const renewalWillCommit =
    renewalFrozenEnabled &&
    Boolean(nrrProgress) &&
    Boolean(didUserTouchSlider[DASHBOARD_GOAL_TYPES.retention]) &&
    retentionPreview() >= GOAL_FLOOR;

  // PAN-796: Save commits the quarter's pilots conversion rate as shown, touched
  // or not (Ben, 2026-10-08) — accepting the estimate is a decision too. Unlike
  // renewal and expansion, a pilots commit is only a %, never a dollar amount.
  // Only for a user the commit endpoint accepts (admins): for anyone else an
  // untouched Save would only be refused.
  const pilotsWillCommit =
    pilotsCommitEnabled &&
    pilotsMapped &&
    nrrProgress.pilots.can_commit === true;
  // Save has something to commit: a touched slider, or the pilots rate.
  const canSave = !saveGateEnabled || anySliderTouched || pilotsWillCommit;

  const handleSave = async () => {
    setSaving(true);
    try {
      const quarterLabel = getQuarterLabel();
      const dueDate = getQuarterEndDate();

      const currency = modalCurrency(summary, nrrProgress, saveGateEnabled);
      const sliderPctsForSave = {
        retention: getValue(DASHBOARD_GOAL_TYPES.retention),
        expansion: getValue(DASHBOARD_GOAL_TYPES.expansion),
        pilot: getValue(DASHBOARD_GOAL_TYPES.pilot),
      };
      // PAN-587: save base × slider (the base the server stamps on commit),
      // never the committed amount — computeGoalTargets pins that, so a stale
      // committed figure would be re-saved verbatim.
      const editing = computeEditingTargets(
        summary,
        nrrProgress,
        sliderPctsForSave,
      );
      const targetByGoalType = {
        [DASHBOARD_GOAL_TYPES.retention]: editing.renewal.target,
        [DASHBOARD_GOAL_TYPES.expansion]: editing.expansion.target,
        [DASHBOARD_GOAL_TYPES.pilot]: editing.pilots.target,
      };

      for (const cfg of visibleSliders) {
        // PAN-587: with the pill flag on, skip buckets whose slider was not
        // moved — an untouched slider would write the shown live estimate
        // back as if the leader had committed it (retroactive-commit harm).
        if (saveGateEnabled && !didUserTouchSlider[cfg.goalType]) {
          continue;
        }
        const pctValue = getValue(cfg.goalType);
        const goalData = {
          goal_type: cfg.goalType,
          goal_category: "monetary",
          monetary_target: targetByGoalType[cfg.goalType],
          monetary_target_currency: currency,
          name: `${quarterLabel} ${cfg.label}`,
          due_date: dueDate,
          description: JSON.stringify({ target_pct: pctValue }),
        };

        const existing = existingGoals[cfg.goalType];
        if (existing) {
          await updateGoal(existing.id, goalData, { silent: true });
        } else {
          await createGoal(goalData, { silent: true });
        }
      }

      // PAN-568 Phase 7 — also commit an expansion leader row to the new
      // team-quarter-goal endpoint. This runs alongside the legacy Goal DB
      // write above so both surfaces stay coherent during the flag rollout.
      // Prefer the API's echoed quarter_label (accounts for team fiscal year
      // offsets); fall back to the calendar-year helper only if we haven't
      // received a response yet.
      //
      // PAN-587: skip when the Expansion slider was not moved — same reason as
      // the loop above.
      const expansionPct = getValue(DASHBOARD_GOAL_TYPES.expansion);
      const expansionTarget = editing.expansion.target;
      const shouldCommitExpansion =
        !saveGateEnabled || didUserTouchSlider[DASHBOARD_GOAL_TYPES.expansion];
      // PAN-639: each leader commit is attempted even if another failed; the
      // toast names exactly the ones that did not save.
      const failed = [];
      // 403: a non-admin can save percentages but not the team's goals.
      let forbidden = false;
      // PAN-796: a refused Pilots commit means the quarter's conversion rate did not change.
      let pilotsForbidden = false;
      try {
        if (shouldCommitExpansion) {
          await revenueMetricsService.commitTeamQuarterGoal({
            quarterLabel:
              nrrProgress?.quarter_label ?? getBackendQuarterLabel(),
            amountMrr: expansionTarget,
            pctAtSet: expansionPct,
          });
        }
      } catch (err) {
        // 403 for non-admins is expected — the endpoint is admin-only.
        // 404 during the pre-deploy window is expected — the endpoint may not
        // exist yet. Neither should block the legacy Goal write from surfacing
        // a success toast, so swallow and continue.
        if (err?.response?.status === 403) forbidden = true;
        if (err?.response?.status !== 403 && err?.response?.status !== 404) {
          // PAN-587: a real error means the Expansion team-quarter leader
          // commit was NOT stored (the legacy Expansion Goal row above may
          // already have been written). Say so and keep the modal open so the
          // leader can retry — the success toast would claim a save that did
          // not happen.
          console.warn("commitTeamQuarterGoal failed:", err);
          failed.push("Expansion");
        }
      }

      // PAN-639: a touched Renewal slider re-freezes the quarter's renewal goal
      // (a leader row with a fresh base and %). Never an untouched slider, and
      // never below the floor, where no goal exists to freeze.
      const renewalTarget = editing.renewal.target;
      if (renewalWillCommit) {
        try {
          await revenueMetricsService.commitTeamQuarterGoal({
            bucket: "renewal",
            quarterLabel:
              nrrProgress?.quarter_label ?? getBackendQuarterLabel(),
            amountMrr: renewalTarget,
            pctAtSet: getValue(DASHBOARD_GOAL_TYPES.retention),
          });
        } catch (err) {
          if (err?.response?.status === 403) forbidden = true;
          if (err?.response?.status !== 403 && err?.response?.status !== 404) {
            console.warn("commitTeamQuarterGoal (renewal) failed:", err);
            failed.push("Retention");
          }
        }
      }

      if (pilotsWillCommit) {
        try {
          await revenueMetricsService.commitTeamQuarterGoal({
            bucket: "pilots",
            quarterLabel:
              nrrProgress?.quarter_label ?? getBackendQuarterLabel(),
            amountMrr: editing.pilots.target,
            pctAtSet: getValue(DASHBOARD_GOAL_TYPES.pilot),
          });
        } catch (err) {
          if (err?.response?.status === 403) pilotsForbidden = true;
          if (err?.response?.status !== 403 && err?.response?.status !== 404) {
            console.warn("commitTeamQuarterGoal (pilots) failed:", err);
            failed.push("Pilots");
          }
        }
      }

      if (
        (forbidden || pilotsForbidden) &&
        (renewalFrozenEnabled || pilotsCommitEnabled)
      ) {
        // PAN-639 / PAN-796: with committed quarter goals, a 403 means the goals
        // in force did not change — "saved successfully" would claim they did.
        // Any other failure in the same Save is named too, not hidden.
        const alsoFailed =
          failed.length > 0
            ? ` The ${failed.join(" and ")} goal${
                failed.length > 1 ? "s" : ""
              } also could not be saved.`
            : "";
        addToast({
          content: `Your percentages were saved, but only a team admin can change the team's goals for this quarter.${alsoFailed}`,
          type: NOTIFICATION_TYPES.error,
        });
        onClose();
        return;
      }

      if (failed.length > 0) {
        // Keep the modal open so the leader can retry; a success toast would
        // claim a save that did not happen.
        addToast({
          content: `Your other goals were saved, but the ${failed.join(" and ")} goal${
            failed.length > 1 ? "s" : ""
          } could not be saved. Please try again.`,
          type: NOTIFICATION_TYPES.error,
        });
        return;
      }

      addToast({
        content: "Team goals saved successfully",
        type: NOTIFICATION_TYPES.success,
      });
      onClose();
    } catch (err) {
      // Toast handled by useGoals hook
    } finally {
      setSaving(false);
    }
  };

  const sliderPcts = {
    retention: getValue(DASHBOARD_GOAL_TYPES.retention),
    expansion: getValue(DASHBOARD_GOAL_TYPES.expansion),
    pilot: getValue(DASHBOARD_GOAL_TYPES.pilot),
  };
  const editing = computeEditingTargets(summary, nrrProgress, sliderPcts);
  const total = editing.total;
  const summaryTargetByKey = {
    renewal: editing.renewal.target,
    expansion: editing.expansion.target,
    pilots: editing.pilots.target,
  };

  // PAN-639: with the flag on, the summary shows the goal IN FORCE for a
  // bucket whose slider was not moved (Save leaves it alone), and the preview
  // only for a moved one. The total then reads "Total after save".
  const inForceByKey = (key) => {
    const raw = nrrProgress?.[key]?.goal?.amount_mrr;
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  // Will Save change this bucket's goal? Renewal: only if it commits (see
  // renewalWillCommit); the others: when their slider was touched.
  const changesOnSave = (cfg) =>
    cfg.summaryKey === "renewal"
      ? renewalWillCommit
      : Boolean(didUserTouchSlider[cfg.goalType]);
  const summaryValue = (cfg) => {
    const preview = summaryTargetByKey[cfg.summaryKey];
    if (!renewalFrozenEnabled || changesOnSave(cfg)) {
      return preview;
    }
    const inForce = inForceByKey(cfg.summaryKey);
    if (inForce !== null) return inForce;
    // No goal in force and a preview below the floor: there is no goal (the
    // card says "No goal"), so it adds nothing.
    return preview < GOAL_FLOOR ? 0 : preview;
  };
  const summaryTotal = renewalFrozenEnabled
    ? visibleSliders.reduce((acc, cfg) => acc + summaryValue(cfg), 0)
    : total;

  const poolByKey = {
    renewal: editing.renewal.base,
    expansion: editing.expansion.base,
    pilots: editing.pilots.base,
  };

  const currency = modalCurrency(summary, nrrProgress, saveGateEnabled);
  const sym = getCurrencySymbol(currency);

  const legacyPortfolioValue = (stat) => {
    if (stat.legacySource === "summary") {
      const raw = summary?.[stat.key]?.total_mrr;
      if (!raw) return `${sym}0`;
      // getFormattedMoney handles Money objects with currency; strip and re-add the
      // ARR uplift by rendering the compact form ourselves so ARR mode multiplies.
      if (revenueUnit === "ARR") {
        const amt = Number(raw.amount ?? raw) || 0;
        return `${sym}${fmt(amt * 12)}`;
      }
      return getFormattedMoney(raw);
    }
    // nrrProgress source — currently only pilots
    return `${sym}${fmt(annualize(getPilotPool(nrrProgress), revenueUnit))}`;
  };
  const portfolioValue = (stat) =>
    saveGateEnabled
      ? `${sym}${fmt(annualize(poolByKey[stat.key], revenueUnit))}`
      : legacyPortfolioValue(stat);
  // The NRR response carries no counts; flag off keeps the summary's for its buckets.
  const portfolioCount = (stat) =>
    !saveGateEnabled && stat.legacySource === "summary"
      ? getCount(summary, stat.key)
      : null;

  return (
    <Modal show={open} onClose={onClose} size="2xl" popup dismissible>
      <div className="rounded-2xl bg-white shadow-none">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 px-8 pb-5 pt-7">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-lg font-extrabold tracking-[-0.3px] text-slate-800">
                {getQuarterLabel()} Team Goals
              </div>
              <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-600">
                CS Lead
              </span>
            </div>
            <div className="mt-1 text-[13px] text-slate-400">
              Set target percentages — these apply to all CSMs based on their
              individual books
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer border-none bg-transparent p-1 text-xl leading-none text-slate-400 transition-colors hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Team portfolio stats */}
          <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">
              Team Portfolio — {getQuarterLabel()}
            </div>
            <div
              className={`grid gap-4 ${
                visiblePortfolioStats.length === 3
                  ? "grid-cols-3"
                  : "grid-cols-2"
              }`}
            >
              {visiblePortfolioStats.map((stat) => (
                <div key={stat.key} data-testid={`portfolio-stat-${stat.key}`}>
                  <div className="text-xs text-slate-500">
                    {pilotsCommitEnabled && stat.key === "pilots"
                      ? "Pilots ending this quarter"
                      : stat.label}
                  </div>
                  <div className="text-lg font-extrabold tracking-[-0.3px] text-slate-800">
                    {portfolioValue(stat)}
                  </div>
                  {portfolioCount(stat) !== null && (
                    <div className="text-[11px] text-slate-400">
                      {portfolioCount(stat)} accounts
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sliders */}
          <div className="mb-7 flex flex-col gap-6">
            {visibleSliders.map((cfg) => {
              const value = getValue(cfg.goalType);
              const fillPct = ((value - cfg.min) / (cfg.max - cfg.min)) * 100;
              const targetAmount = summaryTargetByKey[cfg.summaryKey];
              const poolMrr = poolByKey[cfg.summaryKey];
              const Icon = cfg.icon;

              return (
                <div
                  key={cfg.goalType}
                  className={`rounded-xl border px-6 py-5 ${cfg.bgClass} ${cfg.borderClass}`}
                >
                  <div className="mb-4 flex items-center gap-2">
                    <span className={cfg.iconClass}>
                      <Icon size={16} />
                    </span>
                    <span className={`text-sm font-bold ${cfg.labelClass}`}>
                      {cfg.label}
                    </span>
                    {cfg.historyNote && (
                      <span className="ml-2 text-[11px] text-slate-400">
                        {cfg.historyNote}
                      </span>
                    )}
                  </div>
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <div className="text-[13px] font-semibold text-slate-600">
                        {cfg.question}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        {renewalFrozenEnabled &&
                        data.loaded &&
                        cfg.summaryKey === "renewal" &&
                        targetAmount < GOAL_FLOOR &&
                        (didUserTouchSlider[cfg.goalType] ||
                          inForceByKey(cfg.summaryKey) === null) ? (
                          <>
                            No goal: target under {sym}
                            {GOAL_FLOOR}/mo on {sym}
                            {fmt(annualize(poolMrr, revenueUnit))}
                          </>
                        ) : (
                          <>
                            Team target: {sym}
                            {fmt(annualize(targetAmount, revenueUnit))} of {sym}
                            {fmt(annualize(poolMrr, revenueUnit))}
                          </>
                        )}
                      </div>
                      {renewalFrozenEnabled && (
                        <FrozenBaseLine
                          nrrProgress={nrrProgress}
                          bucket={cfg.summaryKey}
                          label={cfg.baseLabel}
                          sym={sym}
                          fmt={fmt}
                          annualize={(v) => annualize(v, revenueUnit)}
                          onlyWhenMoved
                          className="mt-0.5 block text-[11px] text-slate-400"
                        />
                      )}
                    </div>
                    <span
                      className={`text-[22px] font-extrabold tracking-[-0.5px] ${cfg.valueClass}`}
                    >
                      {value}%
                    </span>
                  </div>
                  <input
                    type="range"
                    aria-label={`${cfg.label} percentage`}
                    min={cfg.min}
                    max={cfg.max}
                    step={cfg.step}
                    value={value}
                    onChange={(e) => {
                      setValues((prev) => ({
                        ...prev,
                        [cfg.goalType]: Number(e.target.value),
                      }));
                      // Same object once this slider is touched, so later
                      // ticks do not re-run the seeding effect (which lists
                      // the flags as a dependency) and cost an extra render.
                      setDidUserTouchSlider((prev) =>
                        prev[cfg.goalType]
                          ? prev
                          : { ...prev, [cfg.goalType]: true },
                      );
                    }}
                    className="w-full cursor-pointer appearance-none rounded-full outline-none"
                    style={{
                      height: "6px",
                      background: `linear-gradient(to right, ${cfg.fillColor} ${fillPct}%, rgb(226,232,240) ${fillPct}%)`,
                    }}
                  />
                </div>
              );
            })}
            {!pilotsMapped && <PilotsNotMappedCard variant="modal" />}
          </div>

          {/* Team goal summary — three-bucket additive (PAN-520) */}
          <div className="mb-6 rounded-[14px] border-2 border-slate-200 bg-slate-50 px-6 py-5">
            <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">
              Team Goal Summary
            </div>
            <div className="mb-4 flex gap-4">
              {visibleSliders.map((cfg) => (
                <div
                  key={cfg.goalType}
                  data-testid={`summary-row-${cfg.summaryKey}`}
                  className="flex flex-1 items-center gap-2"
                >
                  <div
                    className={`h-2.5 w-2.5 flex-shrink-0 rounded-[3px] ${cfg.summaryColor}`}
                  />
                  <span className="text-xs text-slate-500">
                    {cfg.summaryLabel}
                    {renewalFrozenEnabled && changesOnSave(cfg) && (
                      <span className="ml-1 italic text-indigo-500">
                        on save
                      </span>
                    )}
                  </span>
                  <span className="ml-auto text-[13px] font-bold text-slate-800">
                    {sym}
                    {fmt(annualize(summaryValue(cfg), revenueUnit))}
                  </span>
                </div>
              ))}
            </div>
            <div
              data-testid="summary-total"
              className="flex items-center justify-between border-t-2 border-slate-200 pt-3.5"
            >
              <span className="text-[15px] font-bold text-slate-800">
                {renewalFrozenEnabled && visibleSliders.some(changesOnSave)
                  ? "Total after save"
                  : "Total Team Goal"}
              </span>
              <span className="text-[28px] font-extrabold tracking-[-1px] text-indigo-600">
                {sym}
                {fmt(annualize(summaryTotal, revenueUnit))}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="cursor-pointer rounded-[10px] border border-slate-200 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-500 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !data.loaded || !canSave}
              className="cursor-pointer rounded-[10px] border-none bg-indigo-600 px-6 py-2.5 text-[13px] font-bold text-white shadow-[0_2px_8px_rgba(79,70,229,0.3)] transition-colors hover:bg-indigo-700 disabled:opacity-50"
              title={
                !canSave
                  ? "Move a slider to commit — untouched values stay as estimates"
                  : undefined
              }
            >
              {saving ? "Saving..." : "Save Team Goals"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
